import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Search, Store, ShoppingBag, ArrowRight, MapPin,
  SlidersHorizontal, Check, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stable gradient per category/store name — gives the circular rail and image
// fallbacks a Swiggy-style look without inventing imagery we don't have.
const GRADIENTS = [
  'from-orange-400 to-rose-500',
  'from-purple-500 to-indigo-500',
  'from-emerald-400 to-teal-500',
  'from-pink-500 to-fuchsia-500',
  'from-amber-400 to-orange-500',
  'from-sky-400 to-blue-500',
  'from-violet-500 to-purple-600',
  'from-lime-400 to-green-500',
];
const gradientFor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
};

const SORTS = [
  { key: 'relevance', label: 'Relevance' },
  { key: 'name', label: 'Name (A–Z)' },
  { key: 'items', label: 'Most items' },
];

const Marketplace = () => {
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [sort, setSort] = useState('relevance');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/stores`);
        setStores(res.data.stores || []);
        setCategories(res.data.categories || []);
      } catch {
        setStores([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = stores.filter((s) => {
      const matchesCat =
        activeCat === 'all' || s.categories.some((c) => c.toLowerCase() === activeCat.toLowerCase());
      const matchesQuery =
        !q ||
        s.business_name.toLowerCase().includes(q) ||
        s.store_description.toLowerCase().includes(q) ||
        s.categories.some((c) => c.toLowerCase().includes(q));
      return matchesCat && matchesQuery;
    });
    if (sort === 'name') {
      list = [...list].sort((a, b) => a.business_name.localeCompare(b.business_name));
    } else if (sort === 'items') {
      list = [...list].sort((a, b) => b.product_count - a.product_count);
    }
    return list;
  }, [stores, query, activeCat, sort]);

  // SEO: keyword line + JSON-LD built from the live store list so the listing
  // page is crawlable as a real catalogue, not an empty SPA shell.
  const catWords = categories.slice(0, 12).join(', ');
  const metaKeywords =
    `online store, order online, marketplace, food delivery, restaurants, ` +
    `${catWords ? catWords + ', ' : ''}SAANJH stores, shop online Ludhiana, Punjab, India`;
  const ldJson = useMemo(() => {
    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'SAANJH Marketplace — Order Online',
      url: 'https://saanjh.cc/store',
      description:
        'Browse food stalls, restaurants, furniture, electronics and more across SAANJH stores.',
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: stores.length,
        itemListElement: stores.slice(0, 50).map((s, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `https://saanjh.cc/store/${s.slug}`,
          name: s.business_name,
        })),
      },
    };
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://saanjh.cc/' },
        { '@type': 'ListItem', position: 2, name: 'Stores', item: 'https://saanjh.cc/store' },
      ],
    };
    return JSON.stringify([itemList, breadcrumb]);
  }, [stores]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Order Online — Discover Stores Near You | SAANJH</title>
        <meta
          name="description"
          content="Browse food stalls, restaurants, furniture, electronics and more. One marketplace, many stores. Order online with no login."
        />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href="https://saanjh.cc/store" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SAANJH" />
        <meta property="og:title" content="Order Online — Discover Stores Near You | SAANJH" />
        <meta
          property="og:description"
          content="One marketplace, many stores. Food, fashion, furniture, electronics and more — order online with no login."
        />
        <meta property="og:url" content="https://saanjh.cc/store" />
        <meta property="og:image" content="https://saanjh.cc/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Order Online — Discover Stores Near You | SAANJH" />
        <meta
          name="twitter:description"
          content="One marketplace, many stores. Order online with no login."
        />
        <meta name="twitter:image" content="https://saanjh.cc/og-image.jpg" />
        <script type="application/ld+json">{ldJson}</script>
      </Helmet>

      {/* ── Top bar (Swiggy-style: brand + location + search) ───────────────── */}
      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 sm:gap-6 h-16">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center text-white">
                <ShoppingBag className="h-5 w-5" />
              </span>
              <span className="hidden sm:block text-xl font-extrabold tracking-tight text-slate-900">
                SAANJH
              </span>
            </Link>

            <div className="flex items-center gap-1.5 text-sm shrink-0 border-l border-slate-200 pl-3 sm:pl-6">
              <MapPin className="h-4 w-4 text-orange-600" />
              <span className="font-semibold text-slate-900">All stores</span>
            </div>

            {/* Inline search */}
            <div className="relative flex-1 max-w-xl ml-auto">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for stores, cuisines or items"
                data-testid="marketplace-search"
                className="pl-10 h-11 rounded-xl bg-slate-100 border-0 focus-visible:ring-2 focus-visible:ring-orange-500"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── "What's on your mind?" category rail ────────────────────────────── */}
      {categories.length > 0 && (
        <section className="bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3">
              What's on your mind?
            </h2>
            <div className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar py-3">
              {categories.map((cat) => {
                const active = activeCat === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCat(active ? 'all' : cat)}
                    data-testid={`cat-circle-${cat}`}
                    className="flex flex-col items-center gap-2 shrink-0 group"
                  >
                    <span
                      className={`h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br ${gradientFor(
                        cat
                      )} flex items-center justify-center text-white text-2xl font-black shadow-sm origin-center transition-transform group-hover:scale-110 group-active:scale-95 ${
                        active ? 'ring-4 ring-orange-500 ring-offset-2' : ''
                      }`}
                    >
                      {cat.charAt(0).toUpperCase()}
                    </span>
                    <span className="text-xs font-semibold text-slate-700 max-w-[5rem] truncate capitalize">
                      {cat}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Sticky filter / sort bar ────────────────────────────────────────── */}
      <div className="sticky top-16 z-30 bg-slate-50/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <span className="text-sm font-bold text-slate-900 shrink-0">
            {loading ? 'Loading…' : `${filtered.length} store${filtered.length === 1 ? '' : 's'}`}
          </span>

          {/* Sort pills */}
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <SlidersHorizontal className="h-4 w-4 text-slate-500" />
            {SORTS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                data-testid={`sort-${s.key}`}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors ${
                  sort === s.key
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}
              >
                {sort === s.key && <Check className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />}
                {s.label}
              </button>
            ))}
          </div>

          {activeCat !== 'all' && (
            <button
              onClick={() => setActiveCat('all')}
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-3 py-1.5 text-sm font-semibold capitalize"
            >
              {activeCat} <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Store grid ──────────────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-4 capitalize">
          {activeCat === 'all' ? 'All stores' : `${activeCat} stores`}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-white shadow-sm overflow-hidden">
                <div className="h-44 bg-slate-200 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Store className="h-14 w-14 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No stores match your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filtered.map((store) => (
              <Link key={store.slug} to={`/store/${store.slug}`} data-testid={`store-card-${store.slug}`}>
                <Card className="group overflow-hidden h-full rounded-2xl border-0 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  {/* Image */}
                  <div className="relative h-44 bg-slate-100 overflow-hidden">
                    {store.sample_images.length > 0 ? (
                      <div className={`grid h-full ${store.sample_images.length >= 3 ? 'grid-cols-3' : store.sample_images.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {store.sample_images.map((img, i) => (
                          <img
                            key={i}
                            src={img}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className={`h-full flex items-center justify-center bg-gradient-to-br ${gradientFor(store.business_name)}`}>
                        <Store className="h-12 w-12 text-white/80" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />

                    {store.product_count > 0 && (
                      <span className="absolute bottom-3 left-3 rounded-lg bg-white/95 text-slate-900 text-xs font-bold px-2.5 py-1 shadow">
                        {store.product_count} item{store.product_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-center gap-3">
                      {store.logo_url ? (
                        <img
                          src={store.logo_url}
                          alt=""
                          className="h-12 w-12 rounded-xl object-cover border-2 border-white -mt-10 shadow bg-white shrink-0 relative z-10"
                        />
                      ) : null}
                      <h3 className="font-extrabold text-lg text-slate-900 leading-tight line-clamp-1">
                        {store.business_name}
                      </h3>
                    </div>

                    {store.store_description && (
                      <p className="mt-1.5 text-sm text-slate-500 line-clamp-1">{store.store_description}</p>
                    )}

                    {store.categories.length > 0 && (
                      <p className="mt-2.5 text-sm text-slate-600 line-clamp-1 capitalize">
                        {store.categories.slice(0, 3).join(' · ')}
                      </p>
                    )}

                    <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex items-center justify-between">
                      <span className="text-sm font-bold text-orange-600 inline-flex items-center gap-1">
                        Order now
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-0 text-[11px] font-semibold">
                        Open
                      </Badge>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-xs text-slate-400">
          Powered by SAANJH · Discover and order from local stores
        </div>
      </footer>
    </div>
  );
};

export default Marketplace;
